import { shortText } from '../print/rules';
/**
 * Sizing rules for export, kept free of DOM/Fabric so they can be unit-tested.
 *
 * iPad/iPhone Safari cannot draw a canvas larger than 16,777,216 pixels: a bigger canvas silently
 * comes out blank. PNG/JPG are one canvas, so they are scaled down to fit and the user is told.
 * PDF is drawn in tiles, so it always keeps the full size.
 */
export const MAX_CANVAS_PIXELS = 16_777_216;
export const MAX_PDF_PIXELS = 120_000_000;
export const TILE = 2048;
export const PRINT_DPI = 300;

export interface RasterPlan { scale: number; outW: number; outH: number; reduced: boolean }

/** One-canvas export (PNG/JPG): full size, or scaled down to the largest size the device can draw. */
export function rasterPlan(w: number, h: number, maxPixels = MAX_CANVAS_PIXELS): RasterPlan {
  if (!(w > 0 && h > 0)) throw new Error('ขนาดงานไม่ถูกต้อง');
  const full = w * h;
  if (full <= maxPixels) return { scale: 1, outW: Math.round(w), outH: Math.round(h), reduced: false };
  const scale = Math.sqrt(maxPixels / full);
  // floor so the result never goes over the limit after rounding
  let outW = Math.floor(w * scale), outH = Math.floor(h * scale);
  while (outW * outH > maxPixels) { outW--; outH = Math.floor(outW * h / w); }
  return { scale: outW / w, outW, outH, reduced: true };
}

export interface Tile { x: number; y: number; w: number; h: number }

/**
 * Split the page into tiles. Each tile except the last in its row/column is drawn 1px larger so the
 * next tile overlaps it; that hides hairline gaps between tiles in PDF viewers.
 */
export function tilePlan(w: number, h: number, size = TILE): Tile[] {
  const tiles: Tile[] = [];
  for (let y = 0; y < h; y += size) {
    for (let x = 0; x < w; x += size) {
      const tw = Math.min(size + (x + size < w ? 1 : 0), w - x);
      const th = Math.min(size + (y + size < h ? 1 : 0), h - y);
      tiles.push({ x, y, w: tw, h: th });
    }
  }
  return tiles;
}

/** Page size in PDF points: the design's pixels are treated as 300 DPI (the app's print resolution). */
export const pxToPt = (px: number) => px / PRINT_DPI * 72;

/** Returns a Thai error message when the page is too big to build a PDF on a phone or tablet. */
export function checkPdfSize(w: number, h: number): string | null {
  if (w * h > MAX_PDF_PIXELS) {
    return `งานขนาด ${Math.round(w)} × ${Math.round(h)} px ใหญ่เกินกว่าจะสร้าง PDF ในเครื่องได้ (สูงสุดประมาณ ${MAX_PDF_PIXELS / 1e6} ล้านพิกเซล) ลองลดขนาดงานลง`;
  }
  return null;
}

/** File name: design name (unsafe characters removed) + Thai date and time. */
export function exportFileName(name: string, stamp: string, ext: string, page = ''): string {
  // shortText: never cuts a Thai vowel or tone mark off its letter (BUG-6/47); the design name and the page part
  // (" หน้า3 ชื่อหน้า") are shortened apart, so a long design name never cuts off the page number (files keep their order)
  const safe = (s: string) => s.replace(/[\\/:*?"<>|\u0000-\u001f]/g, ' ');
  const clean = (shortText(safe(name), page ? 40 : 60) || 'craftstudio') + (page ? ' ' + shortText(safe(page), 40) : '');
  return `${clean} ${stamp}.${ext}`;
}
